import { Link } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/cn';
import { cropPhase, daysBetween, type Crop } from '@/lib/crop';
import { todayInPH } from '@/lib/phDate';
import { useMembership } from '@/hooks/useMembership';
import { Kicker } from './shared';
import type { PondTab } from './tabs';

interface Segment {
  key: string;
  label: string;
  sub?: string;
  weight: number;
  bar: string;
  tab?: PondTab;
  now?: boolean;
  muted?: boolean;
  hatched?: boolean;
}

const TARGET_DOC = 90;
const fmt = (d: string) => format(parseISO(d), 'MMM d');

function segments(crop: Crop, today: string, canPlan: boolean): Segment[] {
  const phase = cropPhase(crop);
  const out: Segment[] = [];
  const closedEnd = phase === 'closed' ? (crop.actual_harvest_date ?? null) : today;
  const end = closedEnd ?? today;

  if (crop.prep_start_date) {
    const prepEnd = crop.stocking_date ?? today;
    const prepDays = Math.max(daysBetween(crop.prep_start_date, prepEnd) + (crop.stocking_date ? 0 : 1), 1);
    out.push({
      key: 'prep',
      label: phase === 'prep' ? `Pre-stocking · Prep day ${crop.prep_day ?? prepDays}` : 'Pre-stocking',
      sub: crop.stocking_date
        ? `${fmt(crop.prep_start_date)} – ${fmt(crop.stocking_date)} · ${prepDays} ${prepDays === 1 ? 'day' : 'days'}`
        : phase === 'closed'
          ? `From ${fmt(crop.prep_start_date)} · cancelled`
          : `${fmt(crop.prep_start_date)} – today`,
      weight: prepDays,
      bar: 'bg-blue-300',
      tab: 'prestocking',
      now: phase === 'prep',
    });
  }

  if (!crop.stocking_date && phase === 'closed') return out;

  if (!crop.stocking_date) {
    out.push({
      key: 'stock',
      label: 'Stocking',
      sub: crop.planned_stocking_date ? `Planned ${fmt(crop.planned_stocking_date)}` : canPlan ? 'Not planned yet' : undefined,
      weight: Math.max(out[0]?.weight ?? 1, 1) / 2,
      bar: 'bg-amber-200',
      muted: true,
    });
    out.push({ key: 'grow', label: 'Grow-out', sub: `To ~DOC ${TARGET_DOC}`, weight: TARGET_DOC / 2, bar: 'bg-gray-200', muted: true });
    return out;
  }

  const growDays = Math.max(daysBetween(crop.stocking_date, end) + 1, 1);
  out.push({ key: 'stocked', label: 'Stocked', sub: fmt(crop.stocking_date), weight: Math.max(growDays / 25, 1.4), bar: 'bg-amber-500', tab: 'stocking' });
  out.push({
    key: 'grow',
    label:
      phase === 'grow_out' ? `Grow-out · DOC ${growDays} today` : closedEnd ? `Grow-out · DOC 1–${growDays}` : 'Grow-out',
    sub: `${fmt(crop.stocking_date)} – ${phase === 'grow_out' ? 'today' : closedEnd ? fmt(closedEnd) : 'end not recorded'}`,
    weight: growDays,
    bar: phase === 'grow_out' ? 'bg-primary-600' : 'bg-primary-300',
    tab: 'parameters',
    now: phase === 'grow_out',
  });

  if (phase === 'closed') {
    out.push({ key: 'harvest', label: crop.status.charAt(0).toUpperCase() + crop.status.slice(1), sub: closedEnd ? fmt(closedEnd) : '—', weight: Math.max(growDays / 25, 1.4), bar: 'bg-rose-400', tab: 'harvests' });
  } else {
    const target = crop.target_harvest_date;
    const left = target ? daysBetween(today, target) : TARGET_DOC - growDays;
    out.push({
      key: 'harvest',
      label: 'Harvests',
      sub: target ? `Target ${fmt(target)}` : `Target ~DOC ${TARGET_DOC}`,
      weight: Math.max(left, 8),
      bar: 'bg-gray-100',
      hatched: true,
      tab: 'harvests',
      muted: true,
    });
  }
  return out;
}

export function CropTimeline({ crop, pondId }: { crop: Crop; pondId: string }) {
  const { isSupervisorOrAbove } = useMembership();
  const segs = segments(crop, todayInPH(), isSupervisorOrAbove);
  return (
    <section aria-label="Crop timeline" className="flex flex-col gap-2.5 rounded-xl border border-gray-200/80 bg-white px-5 py-4 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Kicker>Crop timeline</Kicker>
        <span className="text-xs text-gray-500">Tap a stage to open it</span>
      </div>
      <div className="relative -my-1.5 overflow-x-auto py-1.5">
        <ol
          className="grid gap-1"
          style={{ gridTemplateColumns: segs.map((s) => `minmax(4.5rem,${s.weight}fr)`).join(' ') }}
        >
          {segs.map((s, i) => {
            const body = (
              <>
                <div
                  className={cn(
                    'relative h-2.5',
                    s.bar,
                    i === 0 && 'rounded-l-full',
                    i === segs.length - 1 && 'rounded-r-full'
                  )}
                  style={s.hatched ? { backgroundImage: 'repeating-linear-gradient(90deg,#e5e7eb 0 8px,#f3f4f6 8px 16px)' } : undefined}
                >
                  {s.now && <span aria-hidden="true" className="absolute -right-0.5 -top-1.5 h-[22px] w-1 rounded bg-gray-900" />}
                </div>
                <span className={cn('text-xs font-semibold', s.muted ? 'text-gray-500' : 'text-gray-900')}>{s.label}</span>
                {s.sub && <span className="text-xs text-gray-500">{s.sub}</span>}
              </>
            );
            return (
              <li key={s.key} className="min-w-0" aria-current={s.now ? 'step' : undefined}>
                {s.tab ? (
                  <Link
                    to="/ponds/$pondId"
                    params={{ pondId }}
                    search={(prev) => ({ ...prev, tab: s.tab })}
                    title={s.sub ? `${s.label} · ${s.sub}` : s.label}
                    className="flex min-w-0 flex-col gap-1.5 rounded-md hover:bg-gray-50"
                  >
                    {body}
                  </Link>
                ) : (
                  <div className="flex min-w-0 flex-col gap-1.5" title={s.sub ? `${s.label} · ${s.sub}` : s.label}>
                    {body}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
